import { createFileRoute, useParams } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import { FilePreviewModal } from '@/components/FilePreview/FilePreviewModal';
import { VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';

import { useGetDeviceProfileHeader } from '../-shared';

import { DeviceFileDeleteDialog } from './-components/DeviceFileDeleteDialog';
import { DeviceFileFolderList } from './-components/DeviceFileFolderList';
import { DeviceFileRenameDialog } from './-components/DeviceFileRenameDialog';
import { DeviceFileTable } from './-components/DeviceFileTable/DeviceFileTable';
import { DeviceFileToolbar } from './-components/DeviceFileToolbar';
import { DeviceFileUploadDialog } from './-components/DeviceFileUploadDialog';
import type { DeviceFolderType } from './-query/type';
import { useDeviceFileStore } from './-store/store';
import { DeviceFilesSearchSchema } from './-zod-schema';

export const Route = createFileRoute('/_staff/devices/$id/_layout/files/')({
  validateSearch: zodValidator(DeviceFilesSearchSchema),
  component: DeviceFilesTab,
});

function DeviceFilesTab() {
  const { id: deviceId } = useParams({
    from: '/_staff/devices/$id/_layout/files/',
  });
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const selectedFolderType = search.folderType ?? null;

  const {
    isPreviewDialogOpen,
    previewingFileName,
    previewingFileUrl,
    previewingMimeType,
    actions,
  } = useDeviceFileStore();

  const { data: headerData, isPending: isHeaderPending } =
    useGetDeviceProfileHeader(deviceId);

  if (isHeaderPending || !headerData) {
    return <Loader />;
  }

  const handleFolderSelect = (folderType: DeviceFolderType | null) => {
    void navigate({
      to: '.',
      search: {
        folderType: folderType ?? undefined,
        pageIndex: 0,
        pageSize: search.pageSize,
      },
      replace: true,
    });
  };

  const handleUpload = () => {
    if (selectedFolderType) {
      actions.openUploadDialog(selectedFolderType);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <DeviceFileToolbar
        deviceName={headerData.name}
        selectedFolderType={selectedFolderType}
        onUpload={handleUpload}
      />

      <VStack className="flex-1 min-h-0 overflow-auto px-12 py-5">
        {selectedFolderType ? (
          <DeviceFileTable
            deviceId={deviceId}
            folderType={selectedFolderType}
          />
        ) : (
          <DeviceFileFolderList onFolderSelect={handleFolderSelect} />
        )}
      </VStack>

      <DeviceFileUploadDialog deviceId={deviceId} />
      <DeviceFileRenameDialog deviceId={deviceId} />
      <DeviceFileDeleteDialog deviceId={deviceId} />

      <FilePreviewModal
        isOpen={isPreviewDialogOpen}
        onClose={actions.closePreviewDialog}
        filename={previewingFileName || ''}
        mimeType={previewingMimeType || undefined}
        onGetFileUrl={() => previewingFileUrl || ''}
      />
    </div>
  );
}
